import { Controller } from '@hotwired/stimulus'

/**
 * Contact Form Controller
 * Handles form validation, submission state, and user feedback
 *
 * Usage:
 *   <form data-controller="public--contact-form"
 *         data-action="submit->public--contact-form#submit">
 *     <input data-public--contact-form-target="field" required>
 *     <button data-public--contact-form-target="submitButton">Send</button>
 *   </form>
 */
export default class extends Controller {
  static targets = ['field', 'submitButton', 'submitText']
  static values = {
    submittingText: { type: String, default: 'Sending...' }
  }

  connect() {
    this.originalButtonText = this.hasSubmitTextTarget
      ? this.submitTextTarget.textContent
      : this.submitButtonTarget?.textContent

    // Add input event listeners for real-time validation feedback
    this.fieldTargets.forEach(field => {
      field.addEventListener('blur', this.validateField.bind(this))
      field.addEventListener('input', this.clearError.bind(this))
    })
  }

  submit(event) {
    // Validate all fields
    let isValid = true
    this.fieldTargets.forEach(field => {
      if (!this.validateField({ target: field })) {
        isValid = false
      }
    })

    if (!isValid) {
      event.preventDefault()
      // Focus the first invalid field
      const firstInvalid = this.fieldTargets.find(f => !f.validity.valid)
      firstInvalid?.focus()
      return
    }

    // Show loading state
    this.setSubmitting(true)
  }

  validateField(event) {
    const field = event.target
    const isValid = field.validity.valid

    if (!isValid) {
      this.showFieldError(field)
    } else {
      this.clearFieldError(field)
    }

    return isValid
  }

  showFieldError(field) {
    field.classList.add('is-invalid')

    // Create or update error message
    let errorEl = field.parentElement.querySelector('.c-contact-error')
    if (!errorEl) {
      errorEl = document.createElement('span')
      errorEl.className = 'c-contact-error'
      errorEl.setAttribute('role', 'alert')
      field.parentElement.appendChild(errorEl)
    }

    // Get validation message
    errorEl.textContent = this.getErrorMessage(field)
  }

  clearFieldError(field) {
    field.classList.remove('is-invalid')
    const errorEl = field.parentElement.querySelector('.c-contact-error')
    errorEl?.remove()
  }

  clearError(event) {
    const field = event.target
    if (field.validity.valid) {
      this.clearFieldError(field)
    }
  }

  getErrorMessage(field) {
    if (field.validity.valueMissing) {
      return 'This field is required'
    }
    if (field.validity.typeMismatch) {
      if (field.type === 'email') {
        return 'Please enter a valid email address'
      }
      return 'Please enter a valid value'
    }
    if (field.validity.tooShort) {
      return `Please enter at least ${field.minLength} characters`
    }
    return field.validationMessage || 'Please enter a valid value'
  }

  setSubmitting(isSubmitting) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = isSubmitting

    if (isSubmitting) {
      this.submitButtonTarget.classList.add('is-loading')
      if (this.hasSubmitTextTarget) {
        this.submitTextTarget.textContent = this.submittingTextValue
      } else {
        this.submitButtonTarget.textContent = this.submittingTextValue
      }
    } else {
      this.submitButtonTarget.classList.remove('is-loading')
      if (this.hasSubmitTextTarget) {
        this.submitTextTarget.textContent = this.originalButtonText
      } else {
        this.submitButtonTarget.textContent = this.originalButtonText
      }
    }
  }
}
